"use client";
import { Icon, Text } from "@zapier/design-system";
import { atom, useAtomValue, useSetAtom } from "jotai";
import {
  canvasAtom,
  isEditDescriptionModalOpenAtom,
  isViewOnlyModeAtom,
  actorsAtomFamily,
  uniqueAppsForAllNodesAsyncAtom,
  uniqueZapIdsAtom,
  zapUsageAsyncAtomFamily,
  hasZslAtom,
  latestTimeSavedAtomFamily,
} from "@zapier/canvas-state";
import classNames from "classnames";
import { Suspense } from "react";
import { formatTimeUnit } from "@zapier/date-canvas";
import { Tooltip } from "@zapier/design-system-beta";
import pluralize from "pluralize";

import { SummaryApps } from "../SummaryApps";
import { SummaryActors } from "../SummaryActors";

import styles from "./SummaryPanel.module.css";

function SuspendedSummaryAppsWrapper() {
  const uniqueApps = useAtomValue(uniqueAppsForAllNodesAsyncAtom);

  if (uniqueApps.length === 0) {
    return null;
  }

  return (
    <div className={styles.navSection}>
      <SummaryApps
        uniqueApps={uniqueApps}
        tooltipPosition="northwest" // so it doesn't get cut off on the left side
      />
    </div>
  );
}

function SummaryAppsWrapper() {
  return (
    <Suspense fallback={null}>
      <SuspendedSummaryAppsWrapper />
    </Suspense>
  );
}

export function SummaryPanel() {
  const canvas = useAtomValue(canvasAtom);
  const setIsEditDescriptionModalOpen = useSetAtom(
    isEditDescriptionModalOpenAtom,
  );
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const hasZsl = useAtomValue(hasZslAtom);

  const buttonText = canvas?.description ? "Edit" : "Add description";

  const actors = useAtomValue(actorsAtomFamily(false));

  return (
    <>
      {!hasZsl && (
        <Suspense fallback={null}>
          <ActivitySection />
        </Suspense>
      )}
      <div className={styles.navSection}>
        <span className={styles.header}>
          <span className={styles.headerChild}>
            <Text type="SmallPrint3">About</Text>
            {!isViewOnlyMode && (
              <button
                onClick={() => setIsEditDescriptionModalOpen(true)}
                className={classNames(styles.descriptionButton, {
                  [styles.editDescriptionButton]: Boolean(canvas?.description),
                })}
              >
                <Text type="SmallPrint2" color="UiPrimaryStrongest">
                  {buttonText}
                </Text>
              </button>
            )}
          </span>
        </span>
        {canvas?.description && (
          <Text type="SmallPrint1" color="GrayWarm10">
            {canvas.description}
          </Text>
        )}
      </div>
      {actors.length > 0 && (
        <div className={styles.navSection}>
          <SummaryActors />
        </div>
      )}
      <SummaryAppsWrapper />
    </>
  );
}

function ActivitySection() {
  const activityData = useAtomValue(activityDataAsyncAtom);
  const { unit, value } = formatTimeUnit({
    unit: "seconds",
    value: String(activityData.timeSavedInSeconds),
  });

  return (
    <div className={styles.navSection}>
      <Text type="SmallPrint3">Past 30 days</Text>
      {/* TODO: Add dropdown */}
      <div className={styles.activityCards}>
        <div className={styles.card}>
          <Text type="SmallPrint1">
            <span
              style={{ display: "flex", gap: 6 }}
              className={styles.iconWrapper}
            >
              Time saved{" "}
              <Tooltip
                allowMultiline={true}
                placement="top"
                content="The number of runs multiplied by the time saved per run."
              >
                <Tooltip.FocusableTrigger>
                  <Icon name="alertInfo" size="14px" />
                </Tooltip.FocusableTrigger>
              </Tooltip>
            </span>
          </Text>
          <Text type="Body2">
            {value === 0 ? "-" : value.toLocaleString()}{" "}
            {pluralize(unit, value)}
          </Text>
        </div>
        <div className={styles.card}>
          <Text type="SmallPrint1">Zap runs</Text>
          <Text type="Body2">
            {activityData.successfulRuns.toLocaleString()}
          </Text>
        </div>
      </div>
    </div>
  );
}

type ActivityData = {
  timeSavedInSeconds: number;
  successfulRuns: number;
};

const activityDataAsyncAtom = atom<Promise<ActivityData>>(async (get) => {
  const uniqueZapIds = get(uniqueZapIdsAtom);

  const zapData = await Promise.all(
    uniqueZapIds.map(async (zapId) => {
      const metadata = await get(latestTimeSavedAtomFamily(zapId));
      const usage = await get(zapUsageAsyncAtomFamily(zapId));
      return { zapId, metadata, usage };
    }),
  );

  const validZapData = zapData.filter((item) => item.usage !== null);

  const timeSavedInSeconds = validZapData.reduce((total, item) => {
    const { unit, value } = item.metadata || {
      unit: "seconds",
      value: "0",
    };
    const runs = item.usage!.success;
    return total + convertToSeconds(unit, value) * runs;
  }, 0);

  const successfulRuns = validZapData.reduce(
    (total, item) => total + item.usage!.success,
    0,
  );

  return { timeSavedInSeconds, successfulRuns };
});

activityDataAsyncAtom.debugLabel = "activityDataAtom";

function convertToSeconds(unit: string, value: string): number {
  const numValue = parseFloat(value);
  switch (unit) {
    case "seconds":
      return numValue;
    case "minutes":
      return numValue * 60;
    case "hours":
      return numValue * 3600;
    case "days":
      return numValue * 86400;
    default:
      throw new Error(`Unknown unit: ${unit}`);
  }
}
